import { api } from './client';
import type {
  AccountAdmin,
  Contact,
  DistrictSchoolLink,
  DistrictSearchResponse,
  EntityDetail,
  Subscription,
} from './types';

export interface DistrictSearchParams {
  name?: string;
  ndid?: string;
  nces?: string;
  state?: string;
  letter?: string;
  accountStatus?: string;
  limit?: number;
  offset?: number;
}

export function searchDistricts(params: DistrictSearchParams = {}): Promise<DistrictSearchResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  return api.get<DistrictSearchResponse>(`/api/v1/districts?${qs.toString()}`);
}

export function getDistrict(ndid: string): Promise<{ district: EntityDetail; schools: DistrictSchoolLink[] }> {
  return api.get(`/api/v1/districts/${encodeURIComponent(ndid)}`);
}

export function createDistrict(body: Record<string, any>): Promise<{ district: EntityDetail }> {
  return api.post(`/api/v1/districts`, body);
}

export function updateDistrict(ndid: string, body: Record<string, any>): Promise<{ district: EntityDetail }> {
  return api.put(`/api/v1/districts/${encodeURIComponent(ndid)}`, body);
}

export function closeDistrict(ndid: string): Promise<unknown> {
  return api.del(`/api/v1/districts/${encodeURIComponent(ndid)}`);
}

export function updateDistrictAccount(ndid: string, body: Record<string, any>): Promise<{ district: EntityDetail }> {
  return api.put(`/api/v1/districts/${encodeURIComponent(ndid)}/account`, body);
}

export function updateDistrictAccountAdmin(
  ndid: string,
  body: Record<string, any>,
): Promise<{ accountAdmin: AccountAdmin }> {
  return api.put(`/api/v1/districts/${encodeURIComponent(ndid)}/account-admin`, body);
}

export function getDistrictSchools(ndid: string): Promise<{ schools: DistrictSchoolLink[] }> {
  return api.get(`/api/v1/districts/${encodeURIComponent(ndid)}/schools`);
}

export function linkDistrictSchool(ndid: string, nid: string): Promise<unknown> {
  return api.post(`/api/v1/districts/${encodeURIComponent(ndid)}/schools`, { nid });
}

export function unlinkDistrictSchool(ndid: string, nid: string): Promise<unknown> {
  return api.del(`/api/v1/districts/${encodeURIComponent(ndid)}/schools/${encodeURIComponent(nid)}`);
}

export function getDistrictContacts(ndid: string): Promise<{ contacts: Contact[] }> {
  return api.get(`/api/v1/districts/${encodeURIComponent(ndid)}/contacts`);
}

export function createDistrictContact(ndid: string, body: Record<string, any>): Promise<{ contact: Contact }> {
  return api.post(`/api/v1/districts/${encodeURIComponent(ndid)}/contacts`, body);
}

export function updateDistrictContact(
  ndid: string,
  id: number,
  body: Record<string, any>,
): Promise<{ contact: Contact }> {
  return api.put(`/api/v1/districts/${encodeURIComponent(ndid)}/contacts/${id}`, body);
}

export function deleteDistrictContact(ndid: string, id: number): Promise<unknown> {
  return api.del(`/api/v1/districts/${encodeURIComponent(ndid)}/contacts/${id}`);
}

export function getDistrictSubscriptions(ndid: string): Promise<{ subscriptions: Subscription[] }> {
  return api.get(`/api/v1/districts/${encodeURIComponent(ndid)}/subscriptions`);
}

export function addDistrictSubscription(
  ndid: string,
  body: Record<string, any>,
): Promise<{ subscription: Subscription }> {
  return api.post(`/api/v1/districts/${encodeURIComponent(ndid)}/subscriptions`, body);
}

export function updateDistrictSubscription(
  ndid: string,
  productId: number,
  body: Record<string, any>,
): Promise<{ subscription: Subscription }> {
  return api.put(`/api/v1/districts/${encodeURIComponent(ndid)}/subscriptions/${productId}`, body);
}

export function deleteDistrictSubscription(ndid: string, productId: number): Promise<unknown> {
  return api.del(`/api/v1/districts/${encodeURIComponent(ndid)}/subscriptions/${productId}`);
}
